/**
 *  this file include all of the functions that are not dependent on any other logic imports
 */

import * as constants from '../utils/constants';

export const ActionButtonSize = 20;

/** Check if the target point is in the dragging area
 * @param  {Object} area dragging area
 * @param  {pos} point target point
 * @returns check result
 */
export const isPointInDraggingArea = function (draggingArea, pos) {
  if (draggingArea) {
    return (
      pos.x >= draggingArea.x &&
      pos.y >= draggingArea.y &&
      pos.x <= draggingArea.x + draggingArea.width &&
      pos.y <= draggingArea.y + draggingArea.height
    );
  }
  return false;
};

/** calculate new pos after a point is rotated clockwise with a certain radian value
 * @param  {pos} originalPos original pos that the target pos rotate around
 * @param  {pos} thisPos target pos before rotating
 * @param  {float} angle rotation angle(radians degrees)
 * @returns new position
 */
export const calNewPosAfterRotating = function (originalPos, thisPos, angle) {
  const x =
    (thisPos.x - originalPos.x) * Math.cos(angle) - (thisPos.y - originalPos.y) * Math.sin(angle) + originalPos.x;
  const y =
    (thisPos.y - originalPos.y) * Math.cos(angle) + (thisPos.x - originalPos.x) * Math.sin(angle) + originalPos.y;
  return { x: Math.round(x), y: Math.round(y) };
};

/** Calculate distance between 2 points
 * @param  {pos} pos1 point 1
 * @param  {pos} pos2 point 2
 * @returns distance between 2 points
 */
export const calDistanceBetween2Points = function (pos1, pos2) {
  return Math.floor(Math.sqrt(Math.pow(pos1.x - pos2.x, 2) + Math.pow(pos1.y - pos2.y, 2)));
};

/** get next 90 degree
 * @param  {int} rotation rotation in degrees
 * @returns next 90 degree
 */
export const getNext90Degrees = function (rotation) {
  let newRotation = 0;
  if (rotation < 90) {
    newRotation = 90;
  } else if (rotation < 180) {
    newRotation = 180;
  } else if (rotation < 270) {
    newRotation = 270;
  }
  return newRotation;
};

/** snap to special radians
 * @param  {float} theta current rotation degrees in radians
 * @param  {float} rotationSnap rotation snap
 * @returns snapped radian value
 */
export const snapDirection = function (theta, rotationSnap) {
  if (Math.abs(theta) < rotationSnap) {
    theta = 0;
  } else if (Math.abs(theta - Math.PI / 2) < rotationSnap) {
    theta = Math.PI / 2;
  } else if (Math.abs(theta - Math.PI) < rotationSnap) {
    theta = Math.PI;
  } else if (Math.abs(theta - (Math.PI * 3) / 2) < rotationSnap) {
    theta = (3 * Math.PI) / 2;
  } else if (Math.abs(theta - 2 * Math.PI) < rotationSnap) {
    theta = 2 * Math.PI;
  }
  return theta;
};

/** convert radian value to degree value
 * @param  {float} radian - radian values
 * @returns {number} degree values
 */
export const convertToDegree = function (radian) {
  return Math.round((radian / Math.PI) * 180);
};

/** convert degree value to radian value
 * @param  {number} degree - degree values
 * @returns {number} radian values
 */
export const convertToRadian = function (degree) {
  return (degree / 180) * Math.PI;
};

/** get current 4 pos of the element after rotating with a angle
 * @param  {pos} originalPos original pos
 * @param  {Object} imageSize current image size
 * @param  {float} angle rotation angle(radians degrees)
 * @returns new 4 pos for the element
 */
export const getClientRect = function (originalPos, imageSize, angle) {
  const originalTopLeft = originalPos;
  const originalTopRight = {
    x: originalTopLeft.x + imageSize.width,
    y: originalTopLeft.y,
  };
  const originalBottomRight = {
    x: originalTopLeft.x + imageSize.width,
    y: originalTopLeft.y + imageSize.height,
  };
  const originalBottomLeft = {
    x: originalTopLeft.x,
    y: originalTopLeft.y + imageSize.height,
  };
  let newPositions = [];
  newPositions.push(originalTopLeft);
  newPositions.push(calNewPosAfterRotating(originalTopLeft, originalTopRight, angle));
  newPositions.push(calNewPosAfterRotating(originalTopLeft, originalBottomRight, angle));
  newPositions.push(calNewPosAfterRotating(originalTopLeft, originalBottomLeft, angle));

  return newPositions;
};

/** get color description by cmyk
 * @param  {string} cmyk - e.g. '100 0 0 0'
 * @returns {string} color description, e.g. 'Red'
 */
export const getColorDesc = function (cmyk) {
  for (let i = 0; i < constants.colors.length; i++) {
    if (cmyk === constants.colors[i][1]) {
      return constants.colors[i][2];
    }
  }
  return 'Custom Color';
};

/** update zindex of element to be largest or smallest
 * @param  {Object} element - text element or image element object
 * @param  {string} direction - layering direction, it can be 'up' or 'down'
 * @param  {number} zIndex - current largest or smallest zindex
 */
export const updateElementZIndex = function (element, direction, zIndex) {
  if (element) {
    if (direction === 'up') {
      zIndex++;
    } else if (direction === 'down') {
      zIndex--;
    }
    element.data.zIndex = zIndex;
  }
};

/** check if the whole element is in the canvas
 * @param  {Object} draggingArea
 * @param  {Object} elementPos - {x, y}
 * @param  {Object} elementSize - {width, height}
 * @param  {Number} direction
 * @returns {boolean} check result
 */
export const isInCanvas = function (draggingArea, elementPos, elementSize, direction) {
  const displayElement = convertRealElementToDisplayElement({ ...elementPos, ...elementSize, direction });
  if (displayElement.x < draggingArea.x) {
    return false;
  }
  if (displayElement.y < draggingArea.y) {
    return false;
  }
  if (displayElement.x + displayElement.width > draggingArea.x + draggingArea.width) {
    return false;
  }
  if (displayElement.y + displayElement.height > draggingArea.y + draggingArea.height) {
    return false;
  }
  return true;
};

export const isTextInCanvas = function (draggingArea, outlineRect, elementSize) {
  if (outlineRect.x < draggingArea.x) {
    return false;
  }
  if (outlineRect.y < draggingArea.y) {
    return false;
  }
  if (outlineRect.x + elementSize.width > draggingArea.x + draggingArea.width) {
    return false;
  }
  if (outlineRect.y + elementSize.height > draggingArea.y + draggingArea.height) {
    return false;
  }
  return true;
};

export const isRectConrnersInDraggingArea = (corners, draggingArea) => {
  return corners.every(
    (pt) =>
      pt.x > draggingArea.x - 1 &&
      pt.y > draggingArea.y - 1 &&
      pt.x < draggingArea.x + draggingArea.width + 1 &&
      pt.y < draggingArea.y + draggingArea.height + 1
  );
};

export const getRotatedElementRange = (rotatedRectPoints) => {
  const partListX = [];
  const partListY = [];
  rotatedRectPoints.forEach((point) => {
    partListX.push(point.x);
    partListY.push(point.y);
  });
  const minX = Math.min(...partListX);
  const minY = Math.min(...partListY);
  const maxX = Math.max(...partListX);
  const maxY = Math.max(...partListY);

  return { minX, minY, maxX, maxY };
};

export const isRoatedElementInCanvas = function (draggingArea, rotatedRectPoints) {
  const { minX, minY, maxX, maxY } = getRotatedElementRange(rotatedRectPoints);

  return (
    minX >= draggingArea.x &&
    maxX <= draggingArea.x + draggingArea.width &&
    minY >= draggingArea.y &&
    maxY <= draggingArea.y + draggingArea.height
  );
};

/** check if the element need to be moved away from the fold line
 * @param  {Object} draggingArea
 * @param  {enum} elementType - constants.elementType
 * @param  {number} displayX - display x
 * @param  {number} displayWidth - display width
 * @returns a boolean value indicates if the element need to be moved away from the fold line
 */
export const isOnFoldLine = function (draggingArea, elementType, displayX, displayWidth) {
  if (draggingArea.isCalendar) {
    if (
      // when it's text element(the display width must be smaller than the side width) or image element with display width smaller than the side width
      elementType === constants.elementType.Text ||
      (elementType === constants.elementType.Image && displayWidth <= draggingArea.sideWidth)
    ) {
      if (displayX + displayWidth - draggingArea.x <= draggingArea.foldLeft) {
        // the whole element is on the left of the fold line
        return false;
      }
      if (displayX - draggingArea.x >= draggingArea.foldRight) {
        // the whole element is on the right of the fold line
        return false;
      }
      return true;
    }
  } else {
    // there's no fold line if it's not calendar
    return false;
  }
};

/** get display size of the element
 * @param  {number} width - real width
 * @param  {number} height - real height
 * @param  {number} direction - direction
 * @returns display size
 */
export const getDisplaySize = function (width, height, direction) {
  let displayWidth = width;
  let displayHeight = height;
  if (direction === 90 || direction === 270) {
    displayWidth = height;
    displayHeight = width;
  }
  return { displayWidth, displayHeight };
};

/**
 * real element: Canvas element, use the original top-left as the original point
 * display element: always use the current top-left as the original point
 * @param  {number} x
 * @param  {number} y
 * @param  {number} width
 * @param  {number} height
 * @param  {number} direction
 * @returns display element: e.g. {x, y, width, height, direction}
 */
export const convertRealElementToDisplayElement = function ({ x, y, width, height, direction }) {
  const displaySize = { x, y, width, height, direction };
  if (direction === 90 || direction === 270) {
    displaySize.width = height;
    displaySize.height = width;
  }
  if (direction === 90) {
    displaySize.x = x - height;
  }
  if (direction === 180) {
    displaySize.x = x - width;
    displaySize.y = y - height;
  }
  if (direction === 270) {
    displaySize.y = y - width;
  }
  return displaySize;
};

/**
 * real element: Canvas element, use the original top-left as the original point
 * display element: always use the current top-left as the original point
 * @param  {number} x
 * @param  {number} y
 * @param  {number} width
 * @param  {number} height
 * @param  {number} direction
 * @returns real element: e.g. {x, y, width, height, direction}
 */
export const convertDisplayElementToRealElement = function ({ x, y, width, height, direction }) {
  const realDimension = { x, y, width, height, direction };
  if (direction === 90 || direction === 270) {
    realDimension.width = height;
    realDimension.height = width;
  }
  if (direction === 90) {
    realDimension.x = x + width;
  }
  if (direction === 180) {
    realDimension.x = x + width;
    realDimension.y = y + height;
  }
  if (direction === 270) {
    realDimension.y = y + height;
  }
  return realDimension;
};

/** convert database position format to display(knova) position format
 * @param  {Object} originalPos - original position
 * @param  {number} originalPos.x - original position x
 * @param  {number} originalPos.y - original position y
 * @param  {Object} elementSize - element size
 * @param  {number} elementSize.width - element size width
 * @param  {number} elementSize.height - element size height
 * @param  {number} direction - rotated degrees
 * @returns {Object} display position {x,y}
 */
export const convertToDisplayPos = function (originalPos, elementSize, direction) {
  if (direction === 90) return { x: originalPos.x + elementSize.height, y: originalPos.y };
  else if (direction === 180) return { x: originalPos.x + elementSize.width, y: originalPos.y + elementSize.height };
  else if (direction === 270) return { x: originalPos.x, y: originalPos.y + elementSize.width };
  else return originalPos;
};

/** convert display(knova) position format to data position format
 * @param  {Object} originalPos - original position
 * @param  {number} originalPos.x - original position x
 * @param  {number} originalPos.y - original position y
 * @param  {Object} elementSize - element size
 * @param  {number} elementSize.width - element size width
 * @param  {number} elementSize.height - element size height
 * @param  {number} direction - rotated degrees
 * @returns {Object} database position {x,y}
 */
export const convertToDatabasePos = function (originalPos, elementSize, direction) {
  if (direction === 90) return { x: originalPos.x - elementSize.height, y: originalPos.y };
  else if (direction === 180) return { x: originalPos.x - elementSize.width, y: originalPos.y - elementSize.height };
  else if (direction === 270) return { x: originalPos.x, y: originalPos.y - elementSize.width };
  else return originalPos;
};

export const horizaonAlignSide = function (draggingArea, displayPos, displaySize, horizonAlign, sideType) {
  if (sideType === constants.calendarSideType.LeftSide) {
    if (horizonAlign === constants.horizonAlign.Left) {
      return draggingArea.x;
    } else if (horizonAlign === constants.horizonAlign.Center) {
      return Math.floor(draggingArea.x + (draggingArea.sideWidth - displaySize.width) / 2);
    } else if (horizonAlign === constants.horizonAlign.Right) {
      return draggingArea.x + draggingArea.foldLeft - displaySize.width;
    }
  } else if (sideType === constants.calendarSideType.RightSide) {
    if (horizonAlign === constants.horizonAlign.Left) {
      return draggingArea.x + draggingArea.foldRight;
    } else if (horizonAlign === constants.horizonAlign.Center) {
      return Math.floor(draggingArea.x + draggingArea.foldRight + (draggingArea.sideWidth - displaySize.width) / 2);
    } else if (horizonAlign === constants.horizonAlign.Right) {
      return draggingArea.x + draggingArea.width - displaySize.width;
    }
  }
  // return default x
  return displayPos.x;
};
export const horizonAlignCanvas = function (draggingArea, displayPos, displaySize, horizonAlign) {
  if (horizonAlign === constants.horizonAlign.Left) {
    return draggingArea.x;
  } else if (horizonAlign === constants.horizonAlign.Center) {
    return Math.floor(draggingArea.x + (draggingArea.width - displaySize.width) / 2);
  } else if (horizonAlign === constants.horizonAlign.Right) {
    return draggingArea.x + draggingArea.width - displaySize.width;
  }
  // return default x
  return displayPos.x;
};

export const verticalAlignCanvas = function (draggingArea, displayPos, displaySize, verticalAlign) {
  if (verticalAlign === constants.verticalAlign.Top) {
    return draggingArea.y;
  } else if (verticalAlign === constants.verticalAlign.Center) {
    return Math.floor(draggingArea.y + (draggingArea.height - displaySize.height) / 2);
  } else if (verticalAlign === constants.verticalAlign.Bottom) {
    return draggingArea.y + draggingArea.height - displaySize.height;
  }
  // return default x
  return displayPos.y;
};
